import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, DataTable, Drawer, Loading, cx } from "./StudioShared";
import { Plus, Sparkles, CheckCircle2, XCircle, Trophy, ClipboardCheck, LayoutTemplate, BarChart3 } from "lucide-react";

const inp = "w-full bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
const SUBJECT_LIST = {
  strategist: "/admin/strategists", mission: "/admin/missions", workflow: "/admin/workflows",
  workflow_run: "/admin/workflow-runs", department: "/admin/departments",
};
const subjLabel = (t, x) => t === "workflow_run" ? `${x.workflow_name} · ${x.status}` : (x.name || x.label || x.id);
const TABS = [["scorecards", "Scorecards", ClipboardCheck], ["templates", "Templates", LayoutTemplate], ["analytics", "Analytics", BarChart3]];

export default function EvaluationCenter() {
  const [tab, setTab] = useState("scorecards");
  return (
    <StudioPage title="Evaluation Center" testid="evaluation-center-page"
      question="Teach AIPOS how well it performs — score strategists, missions, workflows, runs, providers, models and departments with human review or live AI self-evaluation.">
      <Panel className="p-1.5 mb-6 flex flex-wrap gap-1" data-testid="eval-tabs">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`eval-tab-${k}`}
            className={cx("flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-sm transition-colors", tab === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white hover:bg-white/5")}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </Panel>
      {tab === "scorecards" && <Scorecards />}
      {tab === "templates" && <Templates />}
      {tab === "analytics" && <Analytics />}
    </StudioPage>
  );
}

function Scorecards() {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/eval/evaluations").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const approve = async (e, status) => { setBusy(true); try { await api.post(`/admin/eval/evaluations/${e.id}/approve`, { status }); toast.success(`Marked ${status}.`); load(); } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); } };
  const del = async (e) => { if (!window.confirm("Delete this evaluation?")) return; setBusy(true); try { await api.delete(`/admin/eval/evaluations/${e.id}`); load(); } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); } };
  if (!items) return <Loading />;
  const columns = [
    { key: "subject_name", label: "Subject", render: (r) => <div className="min-w-0"><div className="text-white truncate">{r.subject_name}</div><div className="text-slate-500 text-xs">{r.subject_type}</div></div> },
    { key: "template_name", label: "Template" },
    { key: "overall", label: "Overall", render: (r) => <span className={cx("font-mono", r.overall >= 75 ? "text-emerald-400" : r.overall >= 60 ? "text-gold" : "text-orange-400")}>{r.overall}</span> },
    { key: "quality_rating", label: "Quality", render: (r) => r.quality_rating ? "★".repeat(r.quality_rating) : "—" },
    { key: "confidence", label: "Conf", render: (r) => r.confidence != null ? `${Math.round(r.confidence * 100)}%` : "—" },
    { key: "method", label: "Method", render: (r) => <Badge tone={r.method === "ai_self" ? "gold" : "slate"}>{r.method === "ai_self" ? "🟢 AI" : "human"}</Badge> },
    { key: "verdict", label: "Verdict", render: (r) => <Badge tone={r.verdict === "pass" ? "green" : "red"}>{r.verdict}</Badge> },
    { key: "approval_status", label: "Approval", render: (r) => (
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Badge tone={r.approval_status === "approved" ? "green" : r.approval_status === "rejected" ? "red" : "amber"}>{r.approval_status}</Badge>
        {r.approval_status === "pending" && <><button onClick={() => approve(r, "approved")} data-testid={`eval-approve-${r.id}`} disabled={busy} title="Approve" className="text-emerald-400"><CheckCircle2 size={15} /></button>
          <button onClick={() => approve(r, "rejected")} data-testid={`eval-reject-${r.id}`} disabled={busy} title="Reject" className="text-red-400"><XCircle size={15} /></button></>}
        <button onClick={() => del(r)} disabled={busy} title="Delete" className="text-slate-500 hover:text-red-400 ml-1">×</button>
      </div>) },
  ];
  return (
    <div data-testid="eval-scorecards">
      <div className="flex justify-end mb-4"><button onClick={() => setCreating(true)} data-testid="eval-new-btn" className="flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold rounded-sm hover:bg-gold-light"><Plus size={15} /> New Evaluation</button></div>
      <DataTable testid="eval-table" columns={columns} rows={items} empty="No evaluations yet. Score a subject with human review or AI self-evaluation." />
      {creating && <EvalDrawer onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function EvalDrawer({ onClose, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [scores, setScores] = useState({});
  const [quality, setQuality] = useState(4);
  const [confidence, setConfidence] = useState(0.8);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/eval/templates").then((r) => setTemplates(r.data)); }, []);
  const tpl = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);
  useEffect(() => {
    setSubjects([]); setSubjectId("");
    if (tpl && SUBJECT_LIST[tpl.subject_type]) api.get(SUBJECT_LIST[tpl.subject_type]).then((r) => setSubjects(r.data)).catch(() => setSubjects([]));
  }, [tpl]);
  const submitHuman = async () => {
    setBusy(true);
    try {
      await api.post("/admin/eval/evaluations", { template_id: templateId, subject_type: tpl.subject_type, subject_id: subjectId,
        subject_name: subjLabel(tpl.subject_type, subjects.find((s) => (s.id) === subjectId) || {}), scores, quality_rating: quality, confidence, comments, method: "human" });
      toast.success("Evaluation saved."); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const submitAi = async () => {
    setBusy(true);
    try { await api.post("/admin/eval/evaluations/ai", { template_id: templateId, subject_type: tpl.subject_type, subject_id: subjectId });
      toast.success("AI self-evaluation complete."); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const canSubmit = templateId && subjectId;
  return (
    <Drawer open onClose={onClose} testid="eval-drawer" title="New Evaluation" subtitle="Human review or AI self-evaluation">
      <div><SectionLabel className="mb-1">Template</SectionLabel><select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="eval-template" className={inp}><option value="">— choose template —</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.subject_type})</option>)}</select></div>
      {tpl && (
        <div><SectionLabel className="mb-1">Subject</SectionLabel>
          {SUBJECT_LIST[tpl.subject_type] ? (
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} data-testid="eval-subject" className={inp}><option value="">— choose {tpl.subject_type} —</option>{subjects.map((s) => <option key={s.id} value={s.id}>{subjLabel(tpl.subject_type, s)}</option>)}</select>
          ) : (
            <input value={subjectId} onChange={(e) => setSubjectId(e.target.value)} placeholder={`${tpl.subject_type} id (e.g. openai, gpt-4o)`} data-testid="eval-subject-input" className={inp} />
          )}
        </div>
      )}
      {tpl && canSubmit && (
        <>
          <div className="space-y-3"><SectionLabel>Criteria scores (0–100)</SectionLabel>
            {tpl.criteria.map((c) => (
              <div key={c.key} data-testid={`eval-score-${c.key}`}>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-300">{c.label} <span className="text-slate-600">×{c.weight}</span></span><span className="text-gold font-mono">{scores[c.key] ?? 70}</span></div>
                <input type="range" min="0" max="100" value={scores[c.key] ?? 70} onChange={(e) => setScores({ ...scores, [c.key]: Number(e.target.value) })} className="w-full accent-gold" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><SectionLabel className="mb-1">Quality (1–5)</SectionLabel><input type="number" min="1" max="5" value={quality} onChange={(e) => setQuality(Number(e.target.value))} data-testid="eval-quality" className={inp} /></div>
            <div><SectionLabel className="mb-1">Confidence (0–1)</SectionLabel><input type="number" min="0" max="1" step="0.05" value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} data-testid="eval-confidence" className={inp} /></div>
          </div>
          <div><SectionLabel className="mb-1">Comments</SectionLabel><textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="eval-comments" className={inp} /></div>
        </>
      )}
      <div className="flex gap-2 pt-2">
        <button onClick={submitHuman} disabled={busy || !canSubmit} data-testid="eval-save-human" className="flex-1 bg-gold text-navy px-4 py-2.5 text-sm font-semibold rounded-sm hover:bg-gold-light disabled:opacity-50">Save human review</button>
        <button onClick={submitAi} disabled={busy || !canSubmit} data-testid="eval-save-ai" className="flex items-center gap-1.5 border border-gold/40 text-gold px-4 py-2.5 text-sm rounded-sm hover:bg-gold/10 disabled:opacity-50"><Sparkles size={15} /> {busy ? "Scoring…" : "AI Self-Evaluate"}</button>
      </div>
    </Drawer>
  );
}

function Templates() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/eval/templates").then((r) => setItems(r.data)); }, []);
  if (!items) return <Loading />;
  const columns = [
    { key: "name", label: "Template", render: (r) => <span className="text-white flex items-center gap-2">{r.name}{r.is_system && <Badge tone="gold">system</Badge>}</span> },
    { key: "subject_type", label: "Subject", render: (r) => <Badge tone="slate">{r.subject_type}</Badge> },
    { key: "criteria", label: "Criteria", render: (r) => (r.criteria || []).map((c) => c.label).join(", ") },
    { key: "pass_threshold", label: "Pass ≥", render: (r) => r.pass_threshold },
  ];
  return <DataTable testid="eval-templates-table" columns={columns} rows={items} empty="No templates." />;
}

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/eval/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  if (!d.total) return <div className="text-slate-500 text-sm py-10 text-center">No evaluations yet — create scorecards to build analytics.</div>;
  return (
    <div className="space-y-6" data-testid="eval-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Evaluations" value={d.total} testid="eval-an-total" />
        <Metric label="Avg Overall" value={d.avg_overall} testid="eval-an-overall" />
        <Metric label="Pass Rate" value={`${d.pass_rate}%`} status={d.pass_rate >= 70 ? "healthy" : "warning"} testid="eval-an-pass" />
        <Metric label="Avg Quality" value={`${d.avg_quality}★`} testid="eval-an-quality" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-5"><SectionLabel className="mb-3">By Subject Type</SectionLabel>
          <div className="space-y-2">{d.by_subject_type.map((s) => (
            <div key={s.subject_type} className="flex items-center justify-between text-sm border border-white/10 rounded-sm px-3 py-2">
              <span className="text-slate-300">{s.subject_type} <span className="text-slate-600">({s.count})</span></span>
              <span className="text-slate-400">avg <span className="text-gold font-mono">{s.avg_overall}</span> · {s.pass_rate}% pass</span>
            </div>))}
          </div>
        </Panel>
        <Panel className="p-5"><SectionLabel className="mb-3 flex items-center gap-1.5"><Trophy size={13} className="text-gold" /> Rankings</SectionLabel>
          <div className="space-y-1.5">{d.rankings.slice(0, 10).map((r, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-slate-300 truncate"><span className="text-slate-600 mr-2">#{i + 1}</span>{r.subject_name} <span className="text-slate-600 text-xs">{r.subject_type}</span></span>
              <span className="text-gold font-mono shrink-0 ml-2">{r.avg_overall}</span>
            </div>))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
